import React, { FC, ReactNode, useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import classnames from 'classnames';
import { useIsScrolled } from '@saucelabs/sl-util-helpers';
import { records as userRecords } from '@saucelabs/sl-module-user';
import { DSThemeProvider } from '@saucelabs/ds-theme';
import { actions as authActions } from '@saucelabs/sl-container-authentication';
import { ContentLoader } from '@saucelabs/sl-component-loader';

import BannerNotifications from '../components/BannerNotifications';
import AuthenticatedSidebarNew from '../components/AuthenticatedSidebarNew';
import HeaderNew from '../components/HeaderNew';
import UnAuthenticatedSidebarNew from '../components/UnAuthenticatedSidebarNew';
import { useFocusTarget } from '../utils';
import { SKIP_MENU_IDS } from '../components/SkipMenu';

export const DashboardContainerNew: FC<DashboardContainerNewProps> = ({
  children,
  fetchOrgPlans,
  user,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { isScrolled } = useIsScrolled(containerRef);
  const [hasFetchedOrgs, setHasFetchedOrgs] = useState(false);
  const [hasUsageSubscription, setHasUsageSubscription] = useState(false);
  let isFreemiumCapable;

  useEffect(() => {
    fetchOrgPlans()
      .then(({ data }) => {
        setHasUsageSubscription(data.plan_codes?.includes('usage-monthly') ?? false);
      })
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      .catch(() => {})
      .finally(() => {
        setHasFetchedOrgs(true);
      }); // We're fine with failing request
  }, [fetchOrgPlans]);

  try {
    isFreemiumCapable = user?.isFreemiumCapable();
  } catch (error) {
    // Plans information is fetched in the above hook. When data is not yet available, we defer to
    // the next best thing.
    isFreemiumCapable = user?.isFreemiumUser();
  }

  const { className: focusClassName, ...focusProps } = useFocusTarget(SKIP_MENU_IDS.CONTENT);

  return (
    <div
      className="h-screen w-full absolute flex flex-col flex-nowrap"
      id="dashboard-container"
    >
      <BannerNotifications user={user} />
      <DSThemeProvider>
        <HeaderNew isFreemiumCapable={isFreemiumCapable} />
      </DSThemeProvider>
      <div className="flex-grow flex flex-nowrap min-h-0">
        <DSThemeProvider>
          {user?.isAuthenticated() ? (
            <AuthenticatedSidebarNew
              hasUsageSubscription={hasUsageSubscription}
              isFreemiumCapable={isFreemiumCapable}
            />
          ) : (
            <UnAuthenticatedSidebarNew />
          )}
        </DSThemeProvider>
        <div
          ref={containerRef}
          className={classnames('flex-grow overflow-y-auto overflow-x-hidden shadow-sm', focusClassName)}
          data-qa="app-content"
          {...focusProps}
        >
          <div
            className={classnames('h-px -mt-px -top-px sticky left-0 w-full transition-shadow z-100', {
              'shadow-sm': isScrolled,
            })}
          />
          <ContentLoader show={!hasFetchedOrgs}>
            {children}
          </ContentLoader>
        </div>
      </div>
    </div>
  );
};

export interface DashboardContainerNewProps {
  children?: ReactNode;
  // eslint-disable-next-line camelcase
  fetchOrgPlans: () => Promise<{ data: { plan_codes?: string[] } }>;
  user?: userRecords.UserRecord;
}

const mapStateToProps = (state: { user: userRecords.UserRecord }) => ({
  user: state.user,
});

const mapDispatchToProps = {
  fetchOrgPlans: authActions.getOrgPlans,
};

export default connect(mapStateToProps, mapDispatchToProps)(DashboardContainerNew);
